import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { InspectionStore } from '../../../../data/inspection/inspection.store';
import { LoadingStore } from '../../../../data/loading/loading.store';
import {
  InspectionWizardStepEnum,
  RCIPInspectionRequestVM,
  RCIPInspectionWizardVM,
} from '../../../../data/models/coverage-validator-api-models';
import { StepperComponent } from '../../../../ui-common';
import { BusinessInfo } from '../../shared/business-info/business-info';
import { RcipClasses } from '../rcip-classes/rcip-classes';
import { RcipConfirmation } from '../rcip-confirmation/rcip-confirmation';
import { RcipEligibility } from '../rcip-eligibility/rcip-eligibility';
import { RcipOperations } from '../rcip-operations/rcip-operations';
import { RCIP_STEPS, currentAnswers, hasLegacyQuestions, newWizard } from '../rcip-wizard.helpers';

// Owns the RCIP step order and every save; the steps only render and emit their answers.
@Component({
  selector: 'app-rcip-wizard',
  templateUrl: './rcip-wizard.html',
  styleUrl: './rcip-wizard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    StepperComponent,
    BusinessInfo,
    RcipOperations,
    RcipEligibility,
    RcipClasses,
    RcipConfirmation,
  ],
})
export class RcipWizard {
  protected store = inject(InspectionStore);
  protected loadingStore = inject(LoadingStore);
  protected readonly Step = InspectionWizardStepEnum;
  protected readonly stepLabels = RCIP_STEPS.map((s) => s.label);

  // InspectionPage only renders this wizard once an RCIP request has loaded.
  protected request = computed(() => this.store.request() as RCIPInspectionRequestVM);
  protected wizard = computed(() => this.store.wizard() ?? newWizard(this.request().productId));
  protected answers = computed(() => currentAnswers(this.request(), this.store.wizard()));
  protected legacy = computed(() => hasLegacyQuestions(this.request()));
  protected stepIndex = computed(() =>
    Math.max(0, RCIP_STEPS.findIndex((s) => s.step === this.wizard().currentStep)),
  );

  protected async next(changes: Partial<RCIPInspectionWizardVM> = {}): Promise<void> {
    const nextStep = RCIP_STEPS[Math.min(this.stepIndex() + 1, RCIP_STEPS.length - 1)].step;
    if (await this.save({ ...changes, currentStep: nextStep })) window.scrollTo(0, 0);
  }

  protected async back(): Promise<void> {
    const previousStep = RCIP_STEPS[Math.max(this.stepIndex() - 1, 0)].step;
    if (await this.save({ currentStep: previousStep })) window.scrollTo(0, 0);
  }

  protected async submit(): Promise<void> {
    if (await this.save({})) await this.store.complete();
  }

  protected save(changes: Partial<RCIPInspectionWizardVM>): Promise<boolean> {
    // The API reads "productId" to pick the wizard model, and System.Text.Json wants it first.
    const { productId, ...rest } = { ...this.wizard(), ...changes };
    return this.store.save({ productId, ...rest });
  }
}
